import React, { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { logger } from "../../../lib/logger";
import { useBiz, errText } from "./i18n";
import { Card, Row, Empty, Sheet, btnSoft } from "./ui";

const pct = (a, b) => (!b ? "—" : `${a >= b ? "+" : ""}${Math.round(((a - b) / b) * 100)}%`);

const Insights = ({ r, t, lang, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const ins = r.insights;
  const get = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/simple/reports/${r.id}/insights`); onUpdate(data); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const tips = ins ? (lang === "ar" ? ins.tips_ar : ins.tips_en) : [];
  return (
    <div className="mt-5 border-s-2 border-[#9A7445] bg-[#F6F0E6] p-4" data-testid="report-insights">
      <p className="text-sm font-semibold flex items-center gap-2"><Sparkles size={14} className="text-[#9A7445]" />{t("ai_advice")}</p>
      {ins && (
        <div className="mt-2 text-sm text-[#4A443B]">
          <p data-testid="report-insights-summary">{lang === "ar" ? ins.summary_ar : ins.summary_en}</p>
          <ul className="list-disc ps-5 mt-2 space-y-1">{tips.map((x, i) => <li key={x} data-testid={`report-tip-${i}`}>{x}</li>)}</ul>
        </div>
      )}
      <button onClick={get} disabled={busy} data-testid="report-insights-btn" className={`${btnSoft} mt-3 print:hidden`}>
        <Sparkles size={14} />{busy ? t("ai_busy") : ins ? t("ai_refresh") : t("ai_get")}
      </button>
    </div>
  );
};

const ReportBody = ({ r, t, aed, lang, onUpdate }) => {
  const d = r.data;
  return (
    <div data-testid="report-body">
      <p className="text-xs tracking-[0.3em] text-[#9A7445]">OMR PERFUMER</p>
      <p className="text-lg font-semibold mt-1">{t(r.type)} · {r.period_start.slice(0, 10)} → {r.period_end_incl}</p>
      <div className="mt-3">
        <Row label={t("bottles_sold")} value={d.bottles_sold} strong />
        <Row label={t("revenue")} value={aed(d.revenue)} strong />
        <Row label={t("total_expenses")} value={aed(d.expenses)} />
        <Row label={t("est_profit")} value={aed(d.profit)} strong />
        {r.type === "monthly" && <Row label={t("best_seller")} value={d.best_seller || "—"} />}
        <Row label={t("bottles_remaining")} value={d.bottles_remaining} />
      </div>
      {d.by_perfume.length > 0 && (
        <div className="mt-4">{d.by_perfume.map((p) => <Row key={p.name} label={`${p.name} × ${p.qty}`} value={aed(p.revenue)} />)}</div>
      )}
      {r.previous && (
        <p className="text-sm text-[#6B645A] mt-4">{t("vs_prev")}: {t("revenue")} {pct(d.revenue, r.previous.revenue)} · {t("est_profit")} {pct(d.profit, r.previous.profit)} · {t("bottles_sold")} {r.previous.bottles_sold} → {d.bottles_sold}</p>
      )}
      <Insights r={r} t={t} lang={lang} onUpdate={onUpdate} />
      <p className="text-xs text-[#8A8173] mt-4">{t("profit_hint")}</p>
    </div>
  );
};

export function Reports() {
  const { t, aed, lang } = useBiz();
  const [list, setList] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get("/admin/simple/reports").then((r) => setList(r.data)).catch((e) => logger.error("reports", e)), []);
  useEffect(() => { load(); }, [load]);
  const run = async (type) => {
    try {
      const { data } = await api.post("/admin/simple/reports/run", { type });
      load();
      setView(data);
    } catch (e) {
      toast.error(errText(e));
    }
  };
  if (!list) return <p className="text-[#8A8173]">…</p>;
  return (
    <div data-testid="biz-reports">
      <p className="text-sm text-[#6B645A] mb-4">{t("reports_hint")}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => run("weekly")} data-testid="report-run-weekly" className={btnSoft}><RefreshCw size={14} />{t("run_weekly")}</button>
        <button onClick={() => run("monthly")} data-testid="report-run-monthly" className={btnSoft}><RefreshCw size={14} />{t("run_monthly")}</button>
      </div>
      {list.length === 0 ? <Empty>{t("no_reports")}</Empty> : (
        <Card className="!p-0 divide-y divide-[#F1EBE1]" testid="reports-list">
          {list.map((r) => (
            <button key={r.id} onClick={() => setView(r)} data-testid={`report-${r.id}`} className="w-full flex items-center gap-3 px-4 py-3 text-start hover:bg-[#FAF7F2]">
              <div className="flex-1">
                <p className="text-[15px]">{t(r.type)} · {r.period_start.slice(0, 10)} → {r.period_end_incl}</p>
                <p className="text-xs text-[#8A8173]">{r.data.bottles_sold} · {aed(r.data.revenue)}{r.sent_at ? ` · ${t("sent")}` : ""}</p>
              </div>
              <span className="text-sm text-[#9A7445]">{t("view")}</span>
            </button>
          ))}
        </Card>
      )}
      {view && (
        <Sheet title={t(view.type)} onClose={() => setView(null)} testid="report-view">
          <div id="omr-report-print"><ReportBody r={view} t={t} aed={aed} lang={lang} onUpdate={(d) => { setView(d); load(); }} /></div>
          <button onClick={() => window.print()} data-testid="report-pdf" className={`${btnSoft} mt-5 print:hidden`}><Download size={14} />{t("pdf")}</button>
        </Sheet>
      )}
    </div>
  );
}
